import {useQuery} from '@tanstack/react-query'
import {useParams} from 'react-router-dom'
import {Clock, Play, RotateCw, XCircle} from 'lucide-react'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {Stack} from '@/components/shared/Stack'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {IconText} from '@/components/shared/IconText'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {PageLayout} from '@/components/shared/PageLayout'
import {CardSection} from '@/components/shared/CardSection'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink as EntityLinkChip} from '@/components/shared/EntityLink'
import {ErrorLogLink} from '@/components/ErrorLogLink'
import {ProvisioningAction, ProvisioningTaskStatus} from '@/types/provisioning'
import type {ProvisioningTask} from '@/types/provisioning'
import {AppDate, AppInstant, formatTimestamp, formatRelativeTime} from '@/lib/date-utils'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {DetailList} from '@/components/shared/DetailList'
import {DetailField} from '@/components/shared/DetailField'
import {TimelineItem} from '@/components/shared/TimelineItem'
import {MutedText} from '@/components/shared/MutedText'
import {Strong} from '@/components/shared/Strong'
import {StatusText} from '@/components/shared/StatusText'
import {CONCEPT_ICON} from '@/lib/concepts'

function StatusBadge({status}: {status: string}) {
    const i18n = useI18n()
    switch (status) {
        case ProvisioningTaskStatus.QUEUED:
            return <Badge variant="outline" data-testid="status-badge"><Icon icon={Clock} size="xs" className="mr-1"/>{i18n.provisioning.queued}</Badge>
        case ProvisioningTaskStatus.RUNNING:
            return <Badge variant="secondary" data-testid="status-badge"><Icon icon={Play} size="xs" className="mr-1"/>{i18n.provisioning.running}</Badge>
        case ProvisioningTaskStatus.FAILED:
            return <Badge variant="destructive" data-testid="status-badge"><Icon icon={XCircle} size="xs" className="mr-1"/>{i18n.provisioning.failed}</Badge>
        default:
            return <Badge variant="outline" data-testid="status-badge">{status}</Badge>
    }
}

function ActionBadge({action}: {action: string}) {
    const i18n = useI18n()
    switch (action) {
        case ProvisioningAction.GRANT:
            return <Badge variant={BadgeVariant.SUCCESS}>{i18n.provisioning.actionGrant}</Badge>
        case ProvisioningAction.REVOKE:
            return <Badge variant={BadgeVariant.OUTLINE}>{i18n.provisioning.actionRevoke}</Badge>
        case ProvisioningAction.CREATE_ACCOUNT:
            return <Badge variant={BadgeVariant.SUCCESS}>{i18n.provisioning.actionCreateAccount}</Badge>
        case ProvisioningAction.ENABLE_ACCOUNT:
            return <Badge variant={BadgeVariant.SUCCESS}>{i18n.provisioning.actionEnableAccount}</Badge>
        case ProvisioningAction.DISABLE_ACCOUNT:
            return <Badge variant={BadgeVariant.WARNING}>{i18n.provisioning.actionDisableAccount}</Badge>
        case ProvisioningAction.DELETE_ACCOUNT:
            return <Badge variant={BadgeVariant.DESTRUCTIVE}>{i18n.provisioning.actionDeleteAccount}</Badge>
        case ProvisioningAction.UPDATE_ACCOUNT:
            return <Badge variant={BadgeVariant.SECONDARY}>{i18n.provisioning.actionUpdateAccount}</Badge>
        default:
            return <Badge variant="outline">{action}</Badge>
    }
}

export function ProvisioningTaskDetail() {
    const services = useServices()
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    usePageTitle(i18n.provisioning.taskDetailPageTitle)
    const {id} = useParams<{id: string}>()
    const canManage = hasPermission(permissions.provisioning.manage)

    const {data: task, isLoading, error} = useQuery({
        queryKey: ['provisioning-task', id],
        queryFn: () => services.provisioningTasks.detail(id!),
        enabled: !!id,
        retry: false,
    })

    const isQueuedWithBackoff = task?.status === ProvisioningTaskStatus.QUEUED
        && task.nextRetryAt != null
        && AppDate.epochMillis(task.nextRetryAt) > AppDate.epochMillis(AppInstant.now())

    const retryMutation = useEntityMutation({
        mutationFn: () => services.provisioningTasks.retry(id!),
        invalidate: ['provisioning-task', id],
        successMessage: isQueuedWithBackoff
            ? i18n.provisioning.retryTriggered
            : i18n.provisioning.retryQueued,
    })

    const canRetry = task != null
        && canManage
        && (task.status === ProvisioningTaskStatus.FAILED || isQueuedWithBackoff)

    const timelineEntries = task ? buildTimeline(task, i18n) : []

    return (
        <PageLayout
            variant="detail"
            backTo={routes.provisioning.tasks.path()}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(task, 'PROVISIONING_TASK') : undefined)}
            title={i18n.provisioning.taskDetailTitle}
            icon={CONCEPT_ICON.provisioningTaskDetail}
            data-testid="task-detail-title"
            actions={task ? (
                <InlineRow gap="sm" align="center">
                    <StatusBadge status={task.status}/>
                    {canRetry && (
                        <Button
                            data-testid="btn-retry-task"
                            size="sm"
                            onClick={() => retryMutation.mutate()}
                            disabled={retryMutation.isPending}
                        >
                            <IconText icon={RotateCw}>{i18n.common.retry}</IconText>
                        </Button>
                    )}
                </InlineRow>
            ) : undefined}
        >
            {task && (
                <>
                    <DetailsCard
                        title={i18n.provisioning.taskDetailInfoCard}
                        description={i18n.provisioning.taskDetailInfoCardDesc}
                        data-testid="task-info-card"
                        fields={[
                            {
                                label: i18n.provisioning.action,
                                value: <ActionBadge action={task.action}/>,
                            },
                            {
                                label: i18n.provisioning.identity,
                                value: <EntityLinkChip value={task.identity} testId="link-identity"/>,
                            },
                            {
                                label: i18n.provisioning.entitlement,
                                value: task.enforcementObject
                                    ? <EntityLinkChip value={task.enforcementObject}/>
                                    : '—',
                            },
                            {
                                label: i18n.provisioning.targetSystem,
                                value: <EntityLinkChip value={task.targetSystem} testId="link-target-system"/>,
                            },
                            {
                                label: i18n.provisioning.createdAt,
                                value: formatTimestamp(task.createdAt),
                            },
                            {
                                label: i18n.provisioning.retries,
                                value: i18n.provisioning.taskDetailRetryInfo({count: task.retryCount, max: task.maxRetries}),
                            },
                            ...(task.nextRetryAt ? [{
                                label: i18n.provisioning.nextRetryAt,
                                value: (
                                    <span data-testid="next-retry-at">
                                        {formatTimestamp(task.nextRetryAt)}{' '}
                                        <MutedText size="xs">({formatRelativeTime(task.nextRetryAt)})</MutedText>
                                    </span>
                                ),
                            }] : []),
                        ]}
                    />

                    {task.error && (
                        <CardSection
                            title={i18n.provisioning.taskDetailErrorCard}
                            description={i18n.provisioning.taskDetailErrorCardDesc}
                            data-testid="error-card"
                        >
                            <DetailList columns={1}>
                                <DetailField
                                    label={i18n.provisioning.taskDetailErrorMessage}
                                    value={
                                        <StatusText
                                            tone="destructive"
                                            size="sm"
                                            className="font-mono"
                                            data-testid="error-message"
                                        >
                                            {task.error}
                                        </StatusText>
                                    }
                                />
                                {task.errorLogRef && (
                                    <DetailField
                                        label={i18n.provisioning.taskDetailErrorLog}
                                        value={
                                            <InlineRow gap="md">
                                                <ErrorLogLink
                                                    errorLogRef={task.errorLogRef}
                                                    sourceType="ProvisioningTask"
                                                    sourceId={task.id}
                                                />
                                                <TextLink
                                                    to={routes.admin.errorLogs.detail(task.errorLogRef.errorLogId)}
                                                    testId="error-log-link"
                                                >
                                                    {i18n.provisioning.taskDetailErrorLog}
                                                </TextLink>
                                            </InlineRow>
                                        }
                                    />
                                )}
                            </DetailList>
                        </CardSection>
                    )}

                    <CardSection
                        title={i18n.provisioning.taskDetailTimelineCard}
                        description={i18n.provisioning.taskDetailTimelineCardDesc}
                        data-testid="timeline-card"
                    >
                        <Stack gap="sm" data-testid="timeline-entries">
                            {timelineEntries.map((entry, index) => (
                                <TimelineItem key={index}>
                                    <Strong>{entry.label}</Strong>
                                    <MutedText size="xs" block>{entry.time}</MutedText>
                                </TimelineItem>
                            ))}
                        </Stack>
                    </CardSection>
                </>
            )}
        </PageLayout>
    )
}

interface TimelineEntry {
    label: string
    time: string
}

function buildTimeline(task: ProvisioningTask, i18n: ReturnType<typeof useI18n>): TimelineEntry[] {
    const entries: TimelineEntry[] = []

    entries.push({
        label: i18n.provisioning.timelineQueued,
        time: formatRelativeTime(task.createdAt),
    })

    if (task.status === ProvisioningTaskStatus.RUNNING) {
        entries.push({
            label: i18n.provisioning.timelineStarted,
            time: formatRelativeTime(task.updatedAt),
        })
    }

    if (task.status === ProvisioningTaskStatus.FAILED) {
        entries.push({
            label: i18n.provisioning.timelineFailed,
            time: formatRelativeTime(task.updatedAt),
        })
    }

    if (task.retryCount > 0) {
        entries.push({
            label: i18n.provisioning.timelineRetry({count: task.retryCount}),
            time: formatRelativeTime(task.updatedAt),
        })
    }

    return entries
}
